<template>
    <div v-if="stats.length" class="p-4 bg-white border border-gray-200 rounded-lg shadow-sm grid grid-cols-3 gap-4">
        <div class="col-span-1">
            <h2 class="text-lg font-bold mb-4 border-b border-gray-200 pb-2">Climax</h2>
            <p>
                Un grupo de <span class="font-bold">{{ stats.length }}</span> oyentes iniciaron la reproducción del
                cuento,
                y llegaron al final un total de <span class="font-bold">{{ stats[stats.length - 1].usuarios }}</span>
                oyentes.
            </p>
        </div>
        <div class="col-span-2">
            <canvas ref="chartCanvas"></canvas>
        </div>

    </div>
</template>

<script>
import { useTalleresStore } from "@/stores/talleres";
import { Chart, registerables } from "chart.js";

Chart.register(...registerables);
const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`; // Formato MM:SS
};
export default {
    name: "Generaciones",
    props: {
        descripcion: {
            type: String,
            default: "Estadísticas del cuento",
        },
        tipoGrafico: {
            type: String,
            default: "audios",
        },
    },
    computed: {
        stats() {
            let estadisticas = useTalleresStore().estadisticasAudio || {};
            let segundos = estadisticas?.segundosArray || []
            // Quitar los ultimos 2
            segundos = segundos.slice(0, -8);

            return segundos;
        },
    },
    mounted() {
        this.renderChart();
    },
    watch: {
        stats() {
            this.renderChart();
        },
    },
    methods: {
        renderChart() {
            if (!this.stats.length || !this.$refs.chartCanvas) return;

            const ctx = this.$refs.chartCanvas.getContext("2d");

            if (this.chartInstance) {
                this.chartInstance.destroy();
            }

            // Encuentra los puntos de cambio más brusco
            const bruscoIndices = this.findBruscoChanges();

            this.chartInstance = new Chart(ctx, {
                type: "line",
                cubicInterpolationMode: "monotone",
                data: {
                    labels: this.stats.map(d => d.segundo),
                    datasets: [{
                        // label: "Usuarios activos",
                        data: this.stats.map(d => d.usuarios),
                        borderWidth: 3,
                        fill: true,
                        tension: 0.8,
                        borderColor: "rgba(0, 123, 255, 0.7)",
                        backgroundColor: "rgba(0, 123, 255, 0.2)",
                        pointRadius: this.stats.map((d, i, arr) => {
                            // Muestra un puntito mínimo solo si el valor cambia respecto al anterior
                            return i === 0 || d.usuarios !== arr[i - 1].usuarios ? 1 : 0;
                        }),
                        // color del punto rgba(0, 123, 255, 0.7
                        pointBackgroundColor: 'rgba(0, 123, 255, 0.7'

                    }]
                },
                options: {
                    responsive: true,
                    plugins: {
                        legend: {
                            display: false, // Oculta la leyenda general del gráfico
                        },
                        tooltip: {
                            enabled: true,
                            callbacks: {
                                title: function (tooltipItems) {
                                    const segundos = tooltipItems[0].label;
                                    return formatTime(segundos); // Formatea segundos a minutos:segundos
                                },
                                label: function (tooltipItem) {
                                    return `${tooltipItem.raw} oyentes activos`; // Muestra el valor del punto
                                },
                            },
                            // Muestra solo tooltips en puntos relevantes
                            filter: (tooltipItem) => {
                                const index = tooltipItem.dataIndex;
                                const data = tooltipItem.dataset.data;
                                return index === 0 || data[index] !== data[index - 1]; // Mostrar solo si el valor cambia
                            },
                        },
                    },
                    interaction: {
                        mode: "nearest", // Modo para activar tooltips en puntos cercanos
                        intersect: true, // Solo puntos exactos
                    },
                    scales: {
                        x: {
                            grid: { display: false }, // Oculta el grid del eje X
                            ticks: {
                                callback: function (value, index, values) {
                                    // Muestra solo ticks cada 10 segundos
                                    return index % 10 === 0 ? formatTime(value) : '';
                                },
                                color: "rgba(0, 0, 0, 0.3)",
                            },
                            title: { display: true, text: "Tiempo (minutos:segundos)" },
                        },
                        y: {
                            grid: {
                                drawTicks: false, // Oculta los ticks del grid
                                drawBorder: true, // Dibuja el borde
                                color: (context) => {
                                    // Muestra tres líneas: abajo, medio y arriba
                                    const scale = context.chart.scales.y;
                                    const value = context.tick.value;
                                    if (
                                        value === scale.min || // Línea abajo
                                        value === scale.max || // Línea arriba
                                        value === (scale.min + scale.max) / 2 // Línea en el medio
                                    ) {
                                        return 'rgba(0, 0, 0, 0.1)';
                                    }
                                    return 'transparent'; // Oculta las demás
                                },
                            },
                            ticks: {
                                stepSize: (this.stats.length ? Math.ceil((Math.max(...this.stats.map(d => d.usuarios)) - Math.min(...this.stats.map(d => d.usuarios))) / 2) : 1),
                                callback: (value, index, ticks) => {
                                    // Solo el primero y el último
                                    return index === 0 || index === ticks.length - 1 ? value : '';
                                },
                                color: "rgba(0, 0, 0, 0.3)",
                                padding: 10
                            },
                            title: { display: true, text: "Oyentes Activos" },
                        },
                    },
                },
            });
        },
        findBruscoChanges() {
            // Encuentra los índices de los puntos con cambios bruscos
            const changes = this.stats.map((d, i, arr) => {
                if (i === 0) return 0;
                return Math.abs(d.usuarios - arr[i - 1].usuarios);
            });
            const maxChanges = [...changes].sort((a, b) => b - a).slice(0, 2); // Encuentra los dos cambios más bruscos
            return changes
                .map((change, index) => (maxChanges.includes(change) ? index : null))
                .filter(index => index !== null);
        },
    },
};
</script>

<style scoped>
canvas {
    max-width: 100%;
}
</style>
